import {
  clamp,
  cursorSuffix,
  offsetForElement,
  opacityForElement,
  typewriterText,
} from './animationEngine';
import {
  codePanelLayoutForScene,
  elementNarrationEnd,
  elementNarrationStart,
  pickRenderSet,
  renderTimeForElement,
  resolvedLayout,
} from './layout';
import { elementListLevel, formatListDisplay } from './listFormat';
import { drawThemeBackground } from './presentation';

/**
 * @param {object} opts
 * @param {boolean} opts.omitEditableLayers - when true, skip text/images drawn as HTML overlays
 * @param {Record<string, HTMLImageElement>} [opts.imageCache] - preloaded images for export
 */
export function drawLearningPreviewFrame(ctx, renderedTimeline, currentTime, editor = {}, opts = {}) {
  const omit = opts.omitEditableLayers === true;
  const imageCache = opts.imageCache || {};
  const w = renderedTimeline.width || 1280;
  const h = renderedTimeline.height || 720;
  const pal = drawThemeBackground(ctx, renderedTimeline, w, h);

  const scenes = renderedTimeline.scenes || [];
  const animations = renderedTimeline.animations || [];
  const fps = Number(renderedTimeline.fps) || 30;
  const { draw, codeLines, highlightLine, scene, stateTables = [] } = pickRenderSet(
    renderedTimeline.elements || [],
    scenes,
    currentTime,
    renderedTimeline,
  );

  const cp = codePanelLayoutForScene(scene);
  if (codeLines.length) {
    const px = Number(cp.x) || 580;
    const py = Number(cp.y) || 120;
    const panelW = Number(cp.w) || w - px - 40;
    const ph = Math.min(Number(cp.h) || 420, codeLines.length * 28 + 40);
    ctx.fillStyle = pal.codeBg;
    ctx.strokeStyle = pal.codeBorder;
    ctx.lineWidth = 2;
    roundRect(ctx, px, py, panelW, ph, 12);
    ctx.fill();
    ctx.stroke();
    if (!omit) {
      let ly = py + 20;
      codeLines.forEach((line, i) => {
        if (highlightLine === i + 1) {
          ctx.fillStyle = pal.highlight;
          ctx.globalAlpha = 0.3 + 0.3 * Math.sin(currentTime * 4);
          ctx.fillRect(px + 8, ly - 4, panelW - 52, 26);
          ctx.globalAlpha = 1;
        }
        ctx.fillStyle = pal.text;
        ctx.font = '18px Menlo, monospace';
        ctx.fillText(line, px + 16, ly + 16);
        ly += 28;
      });
    }
  }

  for (const tableEl of stateTables) {
    const vars = tableEl.variables || {};
    const entries = Object.entries(vars);
    if (!entries.length) continue;
    const tx = w - 320;
    const ty = 120;
    const tw = 280;
    const rowH = 28;
    const th = 36 + entries.length * rowH + 16;
    ctx.fillStyle = pal.codeBg;
    ctx.strokeStyle = pal.codeBorder;
    ctx.lineWidth = 2;
    roundRect(ctx, tx, ty, tw, th, 10);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = pal.accent;
    ctx.font = 'bold 14px system-ui';
    ctx.fillText('Variables', tx + 12, ty + 24);
    ctx.font = '13px Menlo, monospace';
    ctx.fillStyle = pal.text;
    let ry = ty + 44;
    for (const [key, val] of entries) {
      ctx.fillText(`${key}: ${val}`, tx + 12, ry);
      ry += rowH;
    }
  }

  const overlayTypes = new Set(['scene_title', 'explanation', 'bullet', 'keyword', 'console_output', 'image']);

  for (const el of draw) {
    if (omit && overlayTypes.has(el.type)) continue;
    const layout = resolvedLayout(el);
    const narrEnd = elementNarrationEnd(el);
    const tRender = renderTimeForElement(el, currentTime, fps);
    let op = opacityForElement(el.id, tRender, animations, el);
    if (op < 0.02) continue;
    const age = tRender - elementNarrationStart(el);
    if (el.type === 'scene_title' && age > 4) op *= 0.55;
    else if (['explanation', 'bullet'].includes(el.type) && age > 10) op *= 0.88;
    const { dx, dy, scale } = offsetForElement(el.id, tRender, animations);
    const x = (layout.x || 64) + dx;
    const y = (layout.y || 140) + dy;
    ctx.globalAlpha = op * clamp(scale, 0.5, 1.2);
    if (scale !== 1 && Math.abs(scale - 1) > 0.03) {
      ctx.save();
      ctx.translate(x, y);
      ctx.scale(scale, scale);
      ctx.translate(-x, -y);
    }

    let text = el.content || '';
    if (el.type === 'bullet' || el.type === 'keyword' || elementListLevel(el) > 0) {
      const fmtType = el.type === 'keyword' ? 'bullet' : el.type;
      text = formatListDisplay(text, elementListLevel(el), fmtType);
    }
    if (el.type === 'console_output') {
      const full = currentTime >= narrEnd;
      text = (full
        ? (el.content || '')
        : typewriterText(text, el.id, tRender, animations))
        + (full ? '' : cursorSuffix(el.id, tRender, animations));
      ctx.font = '18px Menlo, monospace';
      ctx.fillStyle = pal.console;
    } else if (el.type === 'scene_title') {
      ctx.font = 'bold 32px system-ui';
      ctx.fillStyle = pal.accent;
    } else if (el.type === 'keyword') {
      ctx.font = '600 18px system-ui';
      ctx.fillStyle = pal.keyword;
    } else if (el.type === 'image' && el.image_url) {
      const imgEl = imageCache[el.id] || imageCache[el.image_url];
      const iw = layout.w || 200;
      const ih = layout.h || 150;
      if (imgEl && imgEl.complete && imgEl.naturalWidth) {
        ctx.drawImage(imgEl, x, y, iw, ih);
      } else {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x, y, iw, ih);
        ctx.strokeStyle = pal.codeBorder;
        ctx.strokeRect(x, y, iw, ih);
      }
    } else {
      ctx.font = '22px system-ui';
      ctx.fillStyle = pal.text;
    }
    if (el.type !== 'image') {
      wrapFillText(ctx, text, x, y, layout.max_width || 480);
    }
    if (scale !== 1 && Math.abs(scale - 1) > 0.03) {
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  const logo = editor.logo || {};
  const logoMode = logo.mode
    || (logo.text && logo.url ? 'both' : logo.url ? 'image' : logo.text ? 'text' : 'none');
  if (logoMode !== 'none') {
    const anchorX = (logo.x ?? 0.92) * w;
    const anchorY = (logo.y ?? 0.06) * h;
    const lo = Number(logo.opacity ?? 0.85);
    let cursorX = anchorX;
    const showImg = logoMode === 'image' || logoMode === 'both';
    const showTxt = logoMode === 'text' || logoMode === 'both';
    if (showImg && logo.url) {
      const imgEl = imageCache.__logo__ || imageCache[logo.url];
      const imgH = 36;
      const imgW = 72;
      if (imgEl && imgEl.complete && imgEl.naturalWidth) {
        const aspect = imgEl.naturalWidth / Math.max(imgEl.naturalHeight, 1);
        const drawW = Math.min(imgW, imgH * aspect);
        cursorX -= drawW + (showTxt ? 8 : 0);
        ctx.globalAlpha = lo;
        ctx.drawImage(imgEl, cursorX, anchorY, drawW, imgH);
        ctx.globalAlpha = 1;
        cursorX += drawW + 8;
      } else {
        cursorX -= imgW;
        ctx.globalAlpha = lo * 0.6;
        ctx.strokeStyle = pal.codeBorder;
        ctx.strokeRect(cursorX, anchorY, imgW, imgH);
        ctx.globalAlpha = 1;
        cursorX += imgW + 8;
      }
    }
    if (showTxt && logo.text) {
      ctx.globalAlpha = lo;
      ctx.font = '16px system-ui';
      ctx.fillStyle = pal.text || '#94a3b8';
      const tx = showImg && logo.url ? cursorX : anchorX - 100;
      ctx.fillText(String(logo.text), tx, anchorY + 24);
      ctx.globalAlpha = 1;
    }
  }
  for (const ov of editor.overlays || []) {
    if (!ov.text) continue;
    ctx.globalAlpha = Number(ov.opacity ?? 0.9);
    ctx.font = '18px system-ui';
    ctx.fillStyle = '#e2e8f0';
    ctx.fillText(ov.text, (ov.x ?? 0.05) * w, (ov.y ?? 0.9) * h);
    ctx.globalAlpha = 1;
  }
}

function roundRect(ctx, x, y, rw, rh, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + rw, y, x + rw, y + rh, r);
  ctx.arcTo(x + rw, y + rh, x, y + rh, r);
  ctx.arcTo(x, y + rh, x, y, r);
  ctx.arcTo(x, y, x + rw, y, r);
  ctx.closePath();
}

function wrapLineHeight(ctx) {
  const size = parseInt(String(ctx.font).match(/(\d+)px/)?.[1] || '22', 10);
  return Math.max(26, Math.round(size * 1.35));
}

function wrapFillText(ctx, text, x, y, maxWidth) {
  const words = String(text).split(' ');
  let line = words[0] || '';
  let cy = y;
  const step = wrapLineHeight(ctx);
  for (let i = 1; i < words.length; i += 1) {
    const test = `${line} ${words[i]}`;
    if (ctx.measureText(test).width < maxWidth) line = test;
    else {
      ctx.fillText(line, x, cy);
      line = words[i];
      cy += step;
    }
  }
  ctx.fillText(line, x, cy);
}
